import { useState } from "react";
import type { CadenceName, GroupKind } from "@hub/shared";
import { Button, Field, Input, Select, Textarea, Spinner } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import { SearchBox } from "@/features/people/SearchBox";
import { ContactChip } from "@/features/people/ContactChip";
import { descendantIds } from "./groupTree";
import s from "../shared.module.css";

export interface GroupDraft {
  name: string;
  description: string;
  kind: GroupKind;
  filter: string;
  color: string;
  reconnectCadence: CadenceName | null;
  /** Nested groups: the parent shown above this one in the sidebar. */
  parentId: string | null;
}

export const GROUP_COLORS = ["#2563eb", "#7c3aed", "#d946ef", "#dc2626", "#d97706", "#16a34a", "#0891b2", "#64748b"];

/** Shared by the create modal and the group settings tab. Smart filters preview live. */
export function GroupForm({ initial, onSubmit, submitLabel, loading, lockKind, onCancel, groupId }: { initial?: Partial<GroupDraft>; onSubmit: (d: GroupDraft) => void; submitLabel: string; loading?: boolean; lockKind?: boolean; onCancel?: () => void; /** When editing: excludes the group and its descendants from the parent list. */ groupId?: string }) {
  const [d, setD] = useState<GroupDraft>({ name: "", description: "", kind: "static", filter: "", color: GROUP_COLORS[0], reconnectCadence: null, parentId: null, ...initial });
  const preview = useEngineQuery("queryContacts", { query: d.filter, limit: 6 }, { enabled: d.kind === "smart" && d.filter.trim().length > 0, placeholderData: (p) => p });
  const groups = useEngineQuery("listGroups", undefined);
  const excluded = groupId ? descendantIds(groups.data ?? [], groupId) : new Set<string>();
  const parentOptions = (groups.data ?? []).filter((g) => !g.isInternal && !excluded.has(g.id)).sort((a, b) => a.name.localeCompare(b.name));
  const set = <K extends keyof GroupDraft>(k: K, v: GroupDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  return (
    <form className="u-col" style={{ gap: 14 }} onSubmit={(e) => { e.preventDefault(); if (d.name.trim()) onSubmit({ ...d, name: d.name.trim() }); }}>
      <Field label="Name"><Input autoFocus value={d.name} onChange={(e) => set("name", e.target.value)} placeholder="Investors, Design friends, Berlin" /></Field>
      <Field label="Description"><Textarea value={d.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="What this group is for" /></Field>
      {!lockKind && (
        <Field label="Type">
          <div className={s.segmented}>
            <button type="button" data-active={d.kind === "static" ? "true" : undefined} onClick={() => set("kind", "static")}>Static: you add people</button>
            <button type="button" data-active={d.kind === "smart" ? "true" : undefined} onClick={() => set("kind", "smart")}>Smart: a saved search</button>
          </div>
        </Field>
      )}
      {d.kind === "smart" && (
        <Field label="Filter" hint="Uses the same grammar as search. People matching it are members automatically.">
          <SearchBox value={d.filter} onChange={(q) => set("filter", q)} placeholder="company:acme location:berlin" debounce={300} />
          {d.filter.trim() && (
            <div style={{ marginTop: 6 }} className="u-row">
              {preview.isFetching && <Spinner />}
              {preview.data && <span className="u-small u-muted">{preview.data.total} {preview.data.total === 1 ? "person matches" : "people match"}</span>}
              {preview.data?.items.slice(0, 5).map((r) => <ContactChip key={r.contactId} contactId={r.contactId} row={r} onClick={() => undefined} />)}
            </div>
          )}
        </Field>
      )}
      <div className="u-row" style={{ gap: 16, alignItems: "flex-start" }}>
        <Field label="Color">
          <div className="u-row" style={{ gap: 6 }}>
            {GROUP_COLORS.map((c) => <span key={c} role="button" aria-label={c} className={s.colorDot} data-active={c === d.color ? "true" : undefined} style={{ background: c }} onClick={() => set("color", c)} />)}
          </div>
        </Field>
        <Field label="Reconnect cadence" hint="Applies to members without their own cadence">
          <Select value={d.reconnectCadence ?? ""} onChange={(e) => set("reconnectCadence", (e.target.value || null) as CadenceName | null)} style={{ width: 160 }}>
            <option value="">None</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </Select>
        </Field>
        <Field label="Parent group" hint="Nests this group under another in the sidebar">
          <Select value={d.parentId ?? ""} onChange={(e) => set("parentId", e.target.value || null)} style={{ width: 180 }} aria-label="Parent group">
            <option value="">None (top level)</option>
            {parentOptions.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
        </Field>
      </div>
      <div className="u-row" style={{ justifyContent: "flex-end" }}>
        {onCancel && <Button variant="ghost" onClick={onCancel}>Cancel</Button>}
        <Button type="submit" variant="primary" loading={loading} disabled={!d.name.trim() || (d.kind === "smart" && !d.filter.trim())}>{submitLabel}</Button>
      </div>
    </form>
  );
}
